import type { FC, KeyboardEvent as ReactKeyboardEvent, ReactNode } from 'react'

import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { cn } from '@/lib/utils'
import { useScrollLock } from '@/hooks/use-scroll-lock'

const FOCUSABLE = 'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'

interface SheetProps {
    open: boolean
    onClose: () => void
    labelledBy: string
    side?: 'right' | 'left' | 'top'
    className?: string
    children: ReactNode
}

// Accessible slide-over dialog: focus trap, Escape to close, scroll lock, focus restored on close.
export const Sheet: FC<SheetProps> = ({ open, onClose, labelledBy, side = 'right', className, children }): ReactNode => {
    const [mounted, setMounted] = useState(false)
    const [present, setPresent] = useState(open)
    const [visible, setVisible] = useState(false)
    const panel = useRef<HTMLDivElement>(null)
    const returnFocus = useRef<HTMLElement | null>(null)

    useEffect(() => setMounted(true), [])

    useEffect(() => {
        if (open) {
            returnFocus.current = document.activeElement as HTMLElement | null
            setPresent(true)

            let second = 0
            const first = requestAnimationFrame(() => {
                second = requestAnimationFrame(() => setVisible(true))
            })

            return () => {
                cancelAnimationFrame(first)
                cancelAnimationFrame(second)
            }
        }

        setVisible(false)

        const timer = window.setTimeout(() => setPresent(false), 380)

        returnFocus.current?.focus?.({ preventScroll: true })

        return () => window.clearTimeout(timer)
    }, [open])

    useEffect(() => {
        if (!visible || !panel.current) return

        const preferred = panel.current.querySelector<HTMLElement>('[data-autofocus]')
        const first = preferred ?? panel.current.querySelector<HTMLElement>(FOCUSABLE)

        first?.focus({ preventScroll: true })
    }, [visible])

    useScrollLock(open)

    const onKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Escape') {
            event.stopPropagation()
            onClose()

            return
        }

        if (event.key !== 'Tab' || !panel.current) return

        const items = Array.from(panel.current.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(item => item.offsetParent !== null)

        if (!items.length) return

        const first = items[0]
        const last = items[items.length - 1]

        if (event.shiftKey && document.activeElement === first) {
            event.preventDefault()
            last.focus()
        }
        else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault()
            first.focus()
        }
    }

    if (!mounted || !present) return null

    return createPortal(
        <div className='fixed inset-0 z-[90]' onKeyDown={onKeyDown}>
            <div
                aria-hidden='true'
                onClick={onClose}
                className={cn(
                    'absolute inset-0 bg-cocoa-900/40 backdrop-blur-[2px] transition-opacity duration-300',
                    visible ? 'opacity-100' : 'opacity-0'
                )}
            />
            <div
                ref={panel}
                role='dialog'
                aria-modal='true'
                aria-labelledby={labelledBy}
                className={cn(
                    'absolute flex flex-col bg-cream-50 shadow-[0_30px_90px_-30px_rgba(42,29,22,0.6)] transition-transform duration-[380ms] ease-[cubic-bezier(0.22,1,0.36,1)]',
                    side === 'right' && 'inset-y-0 right-0 w-full max-w-[440px]',
                    side === 'left' && 'inset-y-0 left-0 w-full max-w-[380px]',
                    side === 'top' && 'inset-x-0 top-0 max-h-[88svh] rounded-b-[32px]',
                    side === 'right' && (visible ? 'translate-x-0' : 'translate-x-full'),
                    side === 'left' && (visible ? 'translate-x-0' : '-translate-x-full'),
                    side === 'top' && (visible ? 'translate-y-0' : '-translate-y-full'),
                    className
                )}
            >
                {children}
            </div>
        </div>,
        document.body
    )
}
